import { graphql } from 'gatsby';
import React, { useEffect, useRef } from 'react';
import StickyBox from 'react-sticky-box';

import AuthorCard from '../components/BlogPost/AuthorCard';
import BlogPostHead from '../components/BlogPost/BlogPostHead';
import BlogPostHeader from '../components/BlogPost/BlogPostHeader';
import RichTextArticle from '../components/Common/RichText/RichTextArticle';
import ScrollTo from '../components/Common/ScrollTo';
import TableOfContents from '../components/Common/TableOfContents/TableOfContents';
import Accordion from '../components/Generic/Accordion';
import StickyNav from '../components/Generic/StickyNav';
import Layout, { BACKGROUND } from '../components/Layouts/Layout';
import AdditionalResources from '../components/PageSections/AdditionalResources';
import PageSection from '../components/PageSections/PageSection';
import FooterNav from '../components/Resources/FooterNav';
import { useSectionRefs } from '../tools/hooks/useSectionRefs';
import * as styles from './blogPost.module.scss';

const buildTableOfContents = (toc = []) =>
  toc.map(({ title, sectionId }) => ({
    title,
    url: `#${sectionId}`,
    id: sectionId,
  }));

const getFallbackOgImage = (post) => {
  const resizeSrc = post?.pageMetaData?.ogImage?.resize?.src;
  const ogFileUrl = post?.pageMetaData?.ogImage?.file?.url;
  const heroImageUrl = post?.heroImage?.file?.url;

  return resizeSrc || ogFileUrl || heroImageUrl || '';
};

const BlogPost = ({ data: { post, latestPostsByCategory }, pageContext, location }) => {
  const backToTopRef = useRef();
  const { hash } = location;

  const mainCategory = post.categories?.[0]?.categoryName;
  const tocItems = buildTableOfContents(post.tableOfContents?.content || []);
  const hasTOC = tocItems.length > 0;

  // Add anchor to top
  if (tocItems.length > 2) {
    tocItems.push({
      title: 'Return to top',
      url: '#top-of-article',
      id: 'top-of-article',
    });
  }

  // Highlight visible TOC section
  const { visibleSection } = useSectionRefs(post.tableOfContents?.content || []);

  useEffect(() => {
    if (!hash || typeof window === 'undefined') return;

    window.requestAnimationFrame(() => {
      const anchor = document.querySelector(hash);
      if (anchor) {
        const offset = anchor.getBoundingClientRect().top + window.scrollY + 28;
        window.scrollTo({ top: offset, behavior: 'smooth' });
      }
    });
  }, [hash]);

  // Event tracking
  useEffect(() => {
    if (typeof window !== 'undefined' && !!window.FS) {
      window.FS.event('Blog Post Viewed', { category: mainCategory });
    }
  }, [mainCategory]);

  // Related posts logic
  const relatedPosts =
    post.additionalResources ||
    latestPostsByCategory
      ?.find((cat) => cat.categoryName === mainCategory)
      ?.blogPosts?.filter((p) => p.slug !== post.slug)
      .slice(0, 3) ||
    [];

  // SEO meta
  const meta = {
    ...(post?.pageMetaData || {}),
    ogImageUrl: getFallbackOgImage(post),
  };

  return (
    <Layout pageMeta={meta} isBlog isDach={pageContext.isDach} background={BACKGROUND.WHITE}>
      <div className="publish-date" data-post-date={post.publishDate} />

      {hasTOC && <StickyNav visibleSection={visibleSection} title={post.tableOfContents?.title} items={tocItems} />}

      <ScrollTo reference={backToTopRef} />
      <div id="top-of-article" />

      <BlogPostHeader post={post} />

      <PageSection slim className={styles.main}>
        {hasTOC && (
          <div className={styles.sidebar}>
            <StickyBox offsetTop={100} offsetBottom={0}>
              <TableOfContents localLinks title="Table of Contents" currentSection={visibleSection} items={tocItems} />
            </StickyBox>
          </div>
        )}

        {post.body?.raw && <RichTextArticle copy={post.body} />}
      </PageSection>

      {post.author && <AuthorCard author={post.author} isExpert={post.subjectMatterExpert} />}

      <FooterNav backLink="/blog/" backLabel="Return to Blog" returnRef={backToTopRef} />

      {post.faq && (
        <PageSection wide slim className={styles.faq}>
          <h3>{post.faq.heading}</h3>
          <Accordion items={post.faq.content} />
        </PageSection>
      )}

      {relatedPosts.length > 0 && (
        <PageSection className={styles.relatedPosts}>
          <AdditionalResources
            section={{ title: 'Related posts', content: relatedPosts }}
            cardColor="white"
            showImage
          />
        </PageSection>
      )}
    </Layout>
  );
};

export default BlogPost;

export const Head = ({ data: { post }, location }) => <BlogPostHead post={post} location={location} />;

export const pageQuery = graphql`
  query GetBlogPost($slug: String!) {
    post: contentfulPageBlogPost(slug: { eq: $slug }) {
      ...BlogPost
    }
    latestPostsByCategory {
      categoryName
      blogPosts {
        ...BlogPostSlim
      }
    }
  }
`;
